<template>
    <div>
        <div class="title">{{ $t(researchTitle) }}</div>
        <b-form-group :disabled="isResearchExpired()">
            <b-row>
                <b-col cols="12" lg="8">
                    <b-form-group :label="$t('enps.form.researchName')" label-class="title-row">
                        <b-form-input
                            :placeholder="$t('enps.form.typeNameForEnpsResearch')"
                            :disabled="isResearchApplied()"
                            v-model="$v.form.name.$model"
                            :state="checkState('name')"
                        />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group :label="$t('enps.form.acceptResponsesUntilDate')" label-class="title-row">
                        <v-date-picker
                            v-model="$v.form.endDate.$model"
                            mode="dateTime"
                            nav-visibility="click"
                            :popover="{ visibility: 'click' }"
                            :showDayPopover="false"
                            show-caps
                            is24hr
                        >
                            <template v-slot="{ inputValue, inputEvents }">
                                <b-form-input
                                    :value="inputValue"
                                    :state="checkState('endDate')"
                                    v-on="inputEvents"
                                    :placeholder="$t('enps.date')"
                                    readonly
                                />
                            </template>
                        </v-date-picker>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-form-group :label="$t('enps.form.researchQuestionOfEnps')" label-class="title-row">
                        <b-form-input
                            :placeholder="$t('enps.form.whichProbabilityIndicateCompanyAsGoodPlaceToWork')"
                            :disabled="isResearchApplied()"
                            v-model="$v.form.ask.$model"
                            :state="checkState('ask')"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-form-group :label="$t('enps.form.whoWillAnswerResearch')" label-class="title-row">
                        <departments
                            class="mb-0"
                            :value.sync="$v.form.departments.$model"
                            :state="checkState('departments')"
                            enableSelectAll
                            :placeholder="$t('enps.form.selectOneOrMoreOrganizationsTeams')"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-form-group
                        :label="$t('enps.form.comments')"
                        label-class="title-row"
                        :disabled="isResearchApplied()"
                    >
                        <b-row align-h="start">
                            <b-col cols="12" lg="4">
                                <b-form-checkbox
                                    v-model="$v.form.enableComments.$model"
                                    :state="checkState('enableComments')"
                                    class="check"
                                >
                                    {{ $t('enps.form.enableCommentFields') }}
                                </b-form-checkbox>
                            </b-col>
                            <b-col>
                                <b-form-checkbox
                                    v-model="$v.form.requiredComments.$model"
                                    :disabled="!$v.form.enableComments.$model"
                                    :state="checkStateRequiredComments('requiredComments')"
                                    class="check"
                                >
                                    {{ $t('enps.form.makeCommentsRequired') }}
                                </b-form-checkbox>
                            </b-col>
                        </b-row>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col class="d-flex justify-content-end">
                    <b-button class="mr-1" size="sm" variant="secondary" @click="onClickCancel">{{
                        $t('form.button.cancel')
                    }}</b-button>
                    <b-button
                        id="btn-enps-create-and-apply-research"
                        v-if="isNewResearch"
                        class="mr-1"
                        size="sm"
                        variant="outline-primary"
                        :disabled="$v.form.$invalid"
                        @click="onClickCreateAndApply"
                        >{{ $t('enps.form.createApplyResearch') }}</b-button
                    >
                    <b-button
                        id="btn-enps-create-research"
                        size="sm"
                        variant="primary"
                        @click="onClickSubmit"
                        :disabled="$v.form.$invalid"
                    >
                        {{ $t(submitButton) }}
                    </b-button>
                </b-col>
            </b-row>
        </b-form-group>
    </div>
</template>
<script>
import i18n from '@/i18n'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { Departments } from '@/views/components/fields'
import { setupCalendar, DatePicker as VDatePicker } from 'v-calendar'
import { DateInput } from '@/views/components/date'
import { getDateWithTime } from '@/lib/date'

setupCalendar({
    locale: i18n.locale,
    navVisibility: 'focus',
    popoverDirection: 'bottom',
    popoverVisibility: 'focus',
    popoverAlign: 'right',
})

export default {
    mixins: [validationMixin],
    components: {
        VDatePicker,
        DateInput,
        Departments,
    },
    props: {
        isNewResearch: false,
        researchTitle: null,
        submitButton: null,
        research: {
            type: Object,
            default: null,
        },
    },
    validations: {
        form: {
            name: {
                required,
            },
            endDate: {
                required,
            },
            ask: {
                required,
            },
            departments: {
                required,
            },
            enableComments: {},
            requiredComments: {},
        },
    },
    data() {
        return {
            form: {
                name: null,
                endDate: null,
                ask: i18n.t('enps.form.whichProbabilityIndicateCompanyAsGoodPlaceToWork'),
                departments: [],
                enableComments: false,
                requiredComments: false,
            },
        }
    },
    mounted() {
        if (this.research !== null) {
            this.form = this.parseResearchToForm(this.research)
        }
    },
    methods: {
        parseResearchToForm({ title, expired_date, question, require_comments, enable_comments, teams }) {
            return {
                name: title,
                ask: question,
                endDate: new Date(expired_date),
                enableComments: enable_comments,
                requiredComments: require_comments,
                departments: teams.map((t) => ({ value: t.id, label: t.name })),
            }
        },
        checkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        checkStateRequiredComments(val) {
            this.toogleWithEnableComments()
            return this.checkState(val)
        },
        toogleWithEnableComments() {
            if (!this.$v.form['enableComments'].$model) {
                this.$v.form.requiredComments.$model = false
            }
        },
        onClickSubmit() {
            if (this.$v.form.$invalid) {
                return
            }

            const data = this.getFormData()
            this.$emit('submit', data)
        },
        onClickCreateAndApply() {
            if (this.$v.form.$invalid) {
                return
            }

            const data = this.getFormData()
            this.$emit('apply-and-create', data)
        },
        getFormData() {
            return {
                company_id: 1,
                title: this.$v.form.name.$model,
                question: this.$v.form.ask.$model,
                require_comments: this.$v.form.requiredComments.$model,
                enable_comments: this.$v.form.enableComments.$model,
                teams: this.parseDepartments(),
                expired_date: this.parseExpiredDate(),
            }
        },
        parseExpiredDate() {
            return getDateWithTime(this.$v.form.endDate.$model)
        },
        parseDepartments() {
            return this.$v.form.departments.$model.map((d) => d.value)
        },
        onClickCancel() {
            this.$emit('cancel')
        },
        isResearchApplied() {
            if (!this.research) {
                return false
            }
            return this.research.applied
        },
        isResearchExpired() {
            if (!this.research) {
                return false
            }
            return this.research.status == 'expired'
        },
    },
}
</script>
<style scoped lang="sass">
$darkBlue: #022B4F

.form-control[readonly]
    background-color: initial

.title
    height: 25px
    font-weight: bold
    font-size: 24px
    line-height: 28px
    color: #022b4f
    margin-bottom: 1.2rem

::v-deep
    .title-row
        height: 29.58px
        font-weight: bold
        font-size: 16px
        line-height: 19px
        color: #808080
        margin-bottom: 0
    .date-input
        margin: 0
        width: 100% !important
    .was-validated .custom-control-input:valid:checked ~ .custom-control-label::before, .custom-control-input.is-valid:checked ~ .custom-control-label::before
        background-color: $darkBlue !important
        border-color: $darkBlue !important
    .was-validated .custom-control-input:valid ~ .custom-control-label::before, .custom-control-input.is-valid ~ .custom-control-label::before
        border-color: $darkBlue
    .was-validated .custom-control-input:valid:focus:not(:checked) ~ .custom-control-label::before, .custom-control-input.is-valid:focus:not(:checked) ~ .custom-control-label::before
        border-color: $darkBlue
    .was-validated .custom-control-input:valid:focus ~ .custom-control-label::before, .custom-control-input.is-valid:focus ~ .custom-control-label::before
        box-shadow: 0 0 0 0.2rem rgba(2, 43, 79, 0.25)
    .was-validated .custom-control-input:valid ~ .custom-control-label, .custom-control-input.is-valid ~ .custom-control-label
        color: #23282c
</style>
